import React, { useEffect, useState } from 'react';
import '../styles/animations.css';

interface ParticleProps {
  x: number;
  y: number;
  id: string;
}

export const BackgroundBubbles: React.FC = () => {
  return (
    <div className="bubbles-container">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="bubble" />
      ))}
    </div>
  );
};

export const ParticleEffect: React.FC<{ x: number; y: number }> = ({ x, y }) => {
  const [particles, setParticles] = useState<ParticleProps[]>([]);

  useEffect(() => {
    const newParticles: ParticleProps[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10;
      const velocity = 50 + Math.random() * 50;
      newParticles.push({
        x: x + Math.cos(angle) * velocity,
        y: y + Math.sin(angle) * velocity,
        id: `particle-${Date.now()}-${i}`
      });
    }
    setParticles(newParticles);

    const timer = setTimeout(() => {
      setParticles([]);
    }, 1000);

    return () => clearTimeout(timer);
  }, [x, y]);

  return (
    <>
      {particles.map(particle => (
        <div
          key={particle.id}
          className="particle"
          style={{
            left: x,
            top: y,
            '--x': `${particle.x - x}px`,
            '--y': `${particle.y - y}px`
          } as React.CSSProperties}
        />
      ))}
    </>
  );
};

export const LoadingSpinner: React.FC<{ size?: 'small' | 'medium' | 'large' }> = ({ 
  size = 'medium' 
}) => {
  const sizeClass = {
    small: 'w-6 h-6',
    medium: 'w-10 h-10',
    large: 'w-16 h-16'
  }[size];

  return (
    <div className={`spinner ${sizeClass}`}>
      <div className="spinner-ring"></div>
      <div className="spinner-ring"></div>
      <div className="spinner-ring"></div>
    </div>
  );
};

export const PulseAnimation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="pulse">
      {children}
    </div>
  );
};

export const GlowEffect: React.FC<{ children: React.ReactNode; color?: string }> = ({ 
  children, 
  color = '#8a2be2' 
}) => {
  return (
    <div 
      className="glow-effect"
      style={{
        '--glow-color': color
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
};

export const ShimmerEffect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="shimmer-wrapper">
      {children}
      <div className="shimmer"></div>
    </div>
  );
};

export const CoinCollectAnimation: React.FC<{ 
  amount: number; 
  x: number; 
  y: number 
}> = ({ amount, x, y }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div 
      className="coin-collect"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        animation: 'coinCollect 1s ease-out forwards',
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#FFD700',
        textShadow: '0 0 10px rgba(255, 215, 0, 0.8)',
        pointerEvents: 'none',
        zIndex: 9999
      }}
    >
      +{amount}
    </div>
  );
};

export const LevelUpAnimation: React.FC<{ level: number }> = ({ level }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="level-up-container">
      <div className="level-up">
        <h2>LEVEL UP!</h2>
        <div className="level-number">{level}</div>
        <div className="stars">
          {[1, 2, 3].map(i => (
            <span key={i} className="star-twinkle delay-{i}00">⭐</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const RippleEffect: React.FC<{ 
  trigger: boolean; 
  x: number; 
  y: number 
}> = ({ trigger, x, y }) => {
  const [ripples, setRipples] = useState<{ id: string; x: number; y: number }[]>([]);

  useEffect(() => {
    if (trigger) {
      const newRipple = {
        id: `ripple-${Date.now()}`,
        x,
        y
      };
      setRipples(prev => [...prev, newRipple]);

      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }
  }, [trigger, x, y]);

  return (
    <>
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="ripple"
          style={{
            position: 'fixed',
            left: ripple.x,
            top: ripple.y,
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.5)',
            transform: 'translate(-50%, -50%)',
            animation: 'ripple 0.6s ease-out forwards',
            pointerEvents: 'none'
          }}
        />
      ))}
    </>
  );
};

export const StarField: React.FC = () => {
  const [stars, setStars] = useState<{ x: number; y: number; size: number; delay: number }[]>([]);

  useEffect(() => {
    const newStars = [];
    for (let i = 0; i < 50; i++) {
      newStars.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        delay: Math.random() * 5
      });
    }
    setStars(newStars);
  }, []);

  return (
    <div className="star-field">
      {stars.map((star, i) => (
        <div
          key={i}
          className="star"
          style={{
            position: 'fixed',
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            background: 'white',
            borderRadius: '50%',
            animation: `starTwinkle ${3 + star.delay}s ease-in-out infinite`,
            animationDelay: `${star.delay}s`,
            pointerEvents: 'none'
          }}
        />
      ))}
    </div>
  );
};

// Стили для spinner
const spinnerStyles = `
  .spinner {
    position: relative;
    display: inline-block;
  }

  .spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px solid transparent;
    border-top-color: #8a2be2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .spinner-ring:nth-child(2) {
    animation-delay: 0.2s;
    border-top-color: #9932cc;
  }

  .spinner-ring:nth-child(3) {
    animation-delay: 0.4s;
    border-top-color: #da70d6;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .shimmer-wrapper {
    position: relative;
    overflow: hidden;
  }

  .glow-effect {
    filter: drop-shadow(0 0 10px var(--glow-color, #8a2be2));
  }

  .level-up-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10000;
    animation: levelUp 2s ease-in-out;
  }

  .level-up h2 {
    font-size: 48px;
    color: #FFD700;
    text-shadow: 0 0 20px rgba(255, 215, 0, 0.8);
    margin: 0;
  }

  .level-up .level-number {
    font-size: 72px;
    font-weight: bold;
    color: #FFD700;
    text-shadow: 0 0 30px rgba(255, 215, 0, 1);
  }

  .star-field {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
  }
`;

// Вставляем стили в head
if (typeof document !== 'undefined' && !document.getElementById('visual-effects-styles')) {
  const style = document.createElement('style');
  style.id = 'visual-effects-styles';
  style.textContent = spinnerStyles;
  document.head.appendChild(style);
}

export default {
  BackgroundBubbles,
  ParticleEffect,
  LoadingSpinner,
  PulseAnimation,
  GlowEffect,
  ShimmerEffect,
  CoinCollectAnimation,
  LevelUpAnimation,
  RippleEffect,
  StarField
};